import { useCallback, useEffect, useRef, useState } from 'react';
import type { MessageInstance } from 'antd/es/message/interface';
import { AudioTranscriptionError, getAsrStatus, getTranscriptionText, transcribeAudio } from '@/api/audio';

const AUDIO_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
const ASR_SAMPLE_RATE = 16000;
const TRANSCRIPTION_TIMEOUT_MS = 60_000;

export const RECORDING_WAVE_BAR_COUNT = 18;

export const formatRecordingDuration = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

const getSupportedAudioMimeType = () => AUDIO_MIME_TYPES.find(mimeType => MediaRecorder.isTypeSupported(mimeType));

const mixAudioBufferToMono = (audioBuffer: AudioBuffer) => {
  const monoSamples = new Float32Array(audioBuffer.length);

  for (let channelIndex = 0; channelIndex < audioBuffer.numberOfChannels; channelIndex += 1) {
    const channelSamples = audioBuffer.getChannelData(channelIndex);
    for (let sampleIndex = 0; sampleIndex < channelSamples.length; sampleIndex += 1) {
      monoSamples[sampleIndex] += channelSamples[sampleIndex] / audioBuffer.numberOfChannels;
    }
  }

  return monoSamples;
};

const resampleAudio = (samples: Float32Array, sourceSampleRate: number, targetSampleRate: number) => {
  if (sourceSampleRate === targetSampleRate) return samples;

  const sampleRateRatio = sourceSampleRate / targetSampleRate;
  const outputLength = Math.max(1, Math.round(samples.length / sampleRateRatio));
  const resampledSamples = new Float32Array(outputLength);

  for (let outputIndex = 0; outputIndex < outputLength; outputIndex += 1) {
    const sourcePosition = outputIndex * sampleRateRatio;
    const sourceIndex = Math.floor(sourcePosition);
    const nextSourceIndex = Math.min(sourceIndex + 1, samples.length - 1);
    const interpolationRatio = sourcePosition - sourceIndex;
    resampledSamples[outputIndex] =
      samples[sourceIndex] * (1 - interpolationRatio) + samples[nextSourceIndex] * interpolationRatio;
  }

  return resampledSamples;
};

const writeAscii = (view: DataView, offset: number, value: string) => {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
};

const encodePcmWav = (samples: Float32Array, sampleRate: number) => {
  const bytesPerSample = 2;
  const wavBuffer = new ArrayBuffer(44 + samples.length * bytesPerSample);
  const view = new DataView(wavBuffer);

  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + samples.length * bytesPerSample, true);
  writeAscii(view, 8, 'WAVE');
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true);
  view.setUint16(32, bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, 'data');
  view.setUint32(40, samples.length * bytesPerSample, true);

  for (let index = 0; index < samples.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, samples[index]));
    view.setInt16(44 + index * bytesPerSample, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }

  return wavBuffer;
};

const convertAudioBlobToWavFile = async (audioBlob: Blob) => {
  const audioContext = new AudioContext();

  try {
    const audioBuffer = await audioContext.decodeAudioData(await audioBlob.arrayBuffer());
    const monoSamples = mixAudioBufferToMono(audioBuffer);
    const resampledSamples = resampleAudio(monoSamples, audioBuffer.sampleRate, ASR_SAMPLE_RATE);
    const wavBuffer = encodePcmWav(resampledSamples, ASR_SAMPLE_RATE);

    return new File([wavBuffer], `recording-${Date.now()}.wav`, { type: 'audio/wav' });
  } finally {
    await audioContext.close();
  }
};

interface UseVoiceRecognitionOptions {
  active: boolean;
  messageApi: Pick<MessageInstance, 'error' | 'warning'>;
  onRecognized: (text: string) => boolean | Promise<boolean>;
}

export const useVoiceRecognition = ({ active, messageApi, onRecognized }: UseVoiceRecognitionOptions) => {
  const [isAsrEnabled, setIsAsrEnabled] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  const waveformAudioContextRef = useRef<AudioContext | null>(null);
  const waveformSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const waveformAnalyserRef = useRef<AnalyserNode | null>(null);
  const waveformAnimationFrameRef = useRef<number | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const transcriptionAbortRef = useRef<AbortController | null>(null);
  const isStartingRecordingRef = useRef(false);
  const discardRecordingRef = useRef(false);
  const isMountedRef = useRef(true);
  const onRecognizedRef = useRef(onRecognized);
  onRecognizedRef.current = onRecognized;

  useEffect(() => {
    if (!active) {
      setIsAsrEnabled(false);
      return;
    }

    const abortController = new AbortController();
    let isActive = true;

    getAsrStatus({ signal: abortController.signal })
      .then(enabled => {
        if (isActive) setIsAsrEnabled(enabled);
      })
      .catch(error => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        console.warn('查询语音识别状态失败，已隐藏语音入口:', error);
        if (isActive) setIsAsrEnabled(false);
      });

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [active]);

  const stopWaveformVisualization = useCallback(() => {
    if (waveformAnimationFrameRef.current !== null) {
      cancelAnimationFrame(waveformAnimationFrameRef.current);
      waveformAnimationFrameRef.current = null;
    }

    waveformSourceRef.current?.disconnect();
    waveformAnalyserRef.current?.disconnect();
    waveformSourceRef.current = null;
    waveformAnalyserRef.current = null;

    const audioContext = waveformAudioContextRef.current;
    waveformAudioContextRef.current = null;
    if (audioContext && audioContext.state !== 'closed') {
      audioContext.close().catch(error => console.warn('关闭录音波形分析失败:', error));
    }
  }, []);

  const startWaveformVisualization = useCallback(
    (stream: MediaStream) => {
      stopWaveformVisualization();

      try {
        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 64;
        analyser.smoothingTimeConstant = 0.72;
        source.connect(analyser);

        waveformAudioContextRef.current = audioContext;
        waveformSourceRef.current = source;
        waveformAnalyserRef.current = analyser;
        const frequencyData = new Uint8Array(analyser.frequencyBinCount);

        const renderWaveform = () => {
          if (waveformAnalyserRef.current !== analyser) return;

          analyser.getByteFrequencyData(frequencyData);
          const bars = waveformRef.current?.children;
          if (bars?.length) {
            for (let index = 0; index < bars.length; index += 1) {
              const frequencyIndex = Math.min(
                frequencyData.length - 1,
                Math.floor((index / bars.length) * frequencyData.length),
              );
              const level = Math.min(1, 0.12 + frequencyData[frequencyIndex] / 110);
              (bars[index] as HTMLElement).style.transform = `scaleY(${level})`;
            }
          }

          waveformAnimationFrameRef.current = requestAnimationFrame(renderWaveform);
        };

        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(error => console.warn('启动录音波形分析失败:', error));
        }
        renderWaveform();
      } catch (error) {
        console.warn('当前环境无法展示实时录音波形:', error);
        stopWaveformVisualization();
      }
    },
    [stopWaveformVisualization],
  );

  const releaseMediaStream = useCallback(() => {
    stopWaveformVisualization();
    mediaStreamRef.current?.getTracks().forEach(track => track.stop());
    mediaStreamRef.current = null;
  }, [stopWaveformVisualization]);

  const isVoiceBusy = useCallback(
    () => isStartingRecordingRef.current || mediaRecorderRef.current !== null || transcriptionAbortRef.current !== null,
    [],
  );

  const cancelTranscription = useCallback(() => {
    transcriptionAbortRef.current?.abort();
  }, []);

  const transcribeRecordedAudio = useCallback(
    async (mimeType: string) => {
      const chunks = audioChunksRef.current;
      audioChunksRef.current = [];
      mediaRecorderRef.current = null;
      releaseMediaStream();

      if (!isMountedRef.current) return;
      if (!chunks.length) {
        messageApi.warning('未识别到声音');
        return;
      }

      const actualMimeType = mimeType || 'audio/webm';
      const audioBlob = new Blob(chunks, { type: actualMimeType });
      if (!audioBlob.size) {
        messageApi.warning('未识别到声音');
        return;
      }

      const abortController = new AbortController();
      transcriptionAbortRef.current = abortController;
      setIsTranscribing(true);
      const timeout = window.setTimeout(() => {
        if (transcriptionAbortRef.current !== abortController) return;
        abortController.abort();
        if (isMountedRef.current) messageApi.error('语音识别超时，请重新录音');
      }, TRANSCRIPTION_TIMEOUT_MS);
      const handleAbort = () => {
        window.clearTimeout(timeout);
        if (transcriptionAbortRef.current === abortController) {
          transcriptionAbortRef.current = null;
          if (isMountedRef.current) setIsTranscribing(false);
        }
      };
      abortController.signal.addEventListener('abort', handleAbort, { once: true });

      try {
        const audioFile = await convertAudioBlobToWavFile(audioBlob);
        if (!isMountedRef.current || abortController.signal.aborted) return;

        const result = await transcribeAudio(audioFile, { signal: abortController.signal });
        if (
          !isMountedRef.current ||
          abortController.signal.aborted ||
          transcriptionAbortRef.current !== abortController
        )
          return;
        window.clearTimeout(timeout);

        const recognizedText = getTranscriptionText(result);
        if (!recognizedText) {
          messageApi.warning('未识别到声音');
          return;
        }

        const accepted = await onRecognizedRef.current(recognizedText);
        if (!accepted && isMountedRef.current && !abortController.signal.aborted) {
          messageApi.warning('语音已识别，但当前无法发送，请稍后重试');
        }
      } catch (error) {
        if (abortController.signal.aborted) return;
        if (error instanceof DOMException && error.name === 'AbortError') return;
        console.error('语音识别失败:', error);
        if (isMountedRef.current) {
          messageApi.error(error instanceof AudioTranscriptionError ? error.message : '语音识别失败，请稍后重试');
        }
      } finally {
        window.clearTimeout(timeout);
        abortController.signal.removeEventListener('abort', handleAbort);
        if (transcriptionAbortRef.current === abortController) {
          transcriptionAbortRef.current = null;
          if (isMountedRef.current) setIsTranscribing(false);
        }
      }
    },
    [messageApi, releaseMediaStream],
  );

  const startRecording = useCallback(async () => {
    if (!active || !isAsrEnabled || isVoiceBusy() || isRecording || isTranscribing) return;

    if (!window.isSecureContext) {
      messageApi.error(
        window.self !== window.top
          ? '当前语音页面嵌入在非安全的 HTTP 页面中，无法使用麦克风'
          : '当前 HTTP 页面无法使用麦克风，请使用 HTTPS 或 localhost',
      );
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      messageApi.error('当前浏览器不支持语音录入');
      return;
    }

    isStartingRecordingRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      if (!isMountedRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      mediaStreamRef.current = stream;
      const mimeType = getSupportedAudioMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      discardRecordingRef.current = false;

      recorder.ondataavailable = event => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        setIsRecording(false);
        setRecordingSeconds(0);
        if (discardRecordingRef.current) {
          discardRecordingRef.current = false;
          audioChunksRef.current = [];
          mediaRecorderRef.current = null;
          releaseMediaStream();
          return;
        }
        transcribeRecordedAudio(recorder.mimeType || mimeType || 'audio/webm').catch(error =>
          console.error('处理录音失败:', error),
        );
      };
      recorder.start();
      setIsRecording(true);
      startWaveformVisualization(stream);
    } catch (error) {
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      releaseMediaStream();
      console.error('无法启动录音:', error);

      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        messageApi.error(
          window.self !== window.top
            ? '麦克风访问被拒绝，请确认宿主页面允许 iframe 使用麦克风，并检查浏览器权限'
            : '麦克风权限未开启，请在浏览器设置中允许访问',
        );
      } else if (error instanceof DOMException && error.name === 'NotFoundError') {
        messageApi.error('未检测到可用的麦克风');
      } else {
        messageApi.error('无法启动录音');
      }
    } finally {
      isStartingRecordingRef.current = false;
    }
  }, [
    active,
    isVoiceBusy,
    isAsrEnabled,
    isRecording,
    isTranscribing,
    messageApi,
    releaseMediaStream,
    startWaveformVisualization,
    transcribeRecordedAudio,
  ]);

  const confirmRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === 'recording') recorder.stop();
  }, []);

  const cancelRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    discardRecordingRef.current = true;
    if (recorder?.state === 'recording') {
      recorder.stop();
      return;
    }

    setIsRecording(false);
    setRecordingSeconds(0);
    audioChunksRef.current = [];
    mediaRecorderRef.current = null;
    releaseMediaStream();
  }, [releaseMediaStream]);

  useEffect(() => {
    if (!isRecording) return;

    const startedAt = Date.now();
    setRecordingSeconds(0);
    const timer = window.setInterval(() => {
      setRecordingSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 250);

    return () => window.clearInterval(timer);
  }, [isRecording]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      transcriptionAbortRef.current?.abort();
      transcriptionAbortRef.current = null;
      isStartingRecordingRef.current = false;
      discardRecordingRef.current = true;
      const recorder = mediaRecorderRef.current;
      if (recorder) {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        if (recorder.state === 'recording') recorder.stop();
      }
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      releaseMediaStream();
    };
  }, [releaseMediaStream]);

  return {
    cancelTranscription,
    isVoiceBusy,
    cancelRecording,
    confirmRecording,
    isAsrEnabled,
    isRecording,
    isTranscribing,
    recordingSeconds,
    startRecording,
    waveformRef,
  };
};
